// ──────────────────────────────────────────────────────────────────────────
// IL TRATTEGGIO
//
// Il bordo `dashed` del browser fa i trattini lunghi quanto è spesso il
// bordo, e la distanza non si sceglie: su un contorno sottile viene un
// tratteggio fitto, che sembra una cucitura. Qui il contorno lo disegna un
// SVG sopra l'elemento, con trattini e spazi più larghi.
//
// Va dentro un elemento con `position: relative` (o comunque posizionato),
// al posto del suo bordo: segue gli angoli arrotondati che gli si dicono —
// uno solo per tutti, o quattro [in alto a sinistra, in alto a destra, in
// basso a destra, in basso a sinistra] come due schede attaccate — e non
// sposta niente perché non occupa spazio. Se l'elemento tiene un bordo
// suo, trasparente, per non cambiare misura quando il tratteggio sparisce,
// `over` dice quanto è spesso: il tratteggio ci va sopra.
// ──────────────────────────────────────────────────────────────────────────

import { useLayoutEffect, useRef, useState } from "react"

// trattino, spazio
export const DASH = "9 7"

// Il contorno con quattro angoli diversi: un rettangolo non lo sa fare, un
// tracciato sì, ma vuole le misure in pixel
export const roundedRectPath = (width, height, [tl, tr, br, bl]) => {
	const fit = r => Math.max(0, Math.min(r, width / 2, height / 2))
	const [a, b, c, d] = [fit(tl), fit(tr), fit(br), fit(bl)]
	return [
		`M ${a} 0`,
		`H ${width - b}`,
		`A ${b} ${b} 0 0 1 ${width} ${b}`,
		`V ${height - c}`,
		`A ${c} ${c} 0 0 1 ${width - c} ${height}`,
		`H ${d}`,
		`A ${d} ${d} 0 0 1 0 ${height - d}`,
		`V ${a}`,
		`A ${a} ${a} 0 0 1 ${a} 0`,
		"Z",
	].join(" ")
}

// Quanto è grande l'SVG, per disegnarci dentro il tracciato
const useSize = (ref, enabled) => {
	const [size, setSize] = useState(null)
	useLayoutEffect(() => {
		const element = ref.current
		if (!enabled || !element || typeof ResizeObserver === "undefined") return
		const observer = new ResizeObserver(([entry]) =>
			setSize({ width: entry.contentRect.width, height: entry.contentRect.height }),
		)
		observer.observe(element)
		return () => observer.disconnect()
	}, [ref, enabled])
	return size
}

function DashedOutline({ color, width = 1.5, radius = 0, dash = DASH, opacity = 1, over = 0 }) {
	// il tratto sta a cavallo della linea: rientrando di mezzo tratto il suo
	// bordo esterno coincide con quello dell'elemento
	const inset = width / 2 - over
	const corners = Array.isArray(radius) ? radius.map(r => Math.max(0, r - width / 2)) : null
	const ref = useRef(null)
	const size = useSize(ref, Boolean(corners))
	const stroke = {
		stroke: color,
		strokeWidth: width,
		strokeDasharray: dash,
		strokeOpacity: opacity,
		strokeLinecap: "round",
	}

	return (
		<svg
			ref={ref}
			aria-hidden="true"
			focusable="false"
			style={{
				position: "absolute",
				top: inset,
				left: inset,
				width: `calc(100% - ${2 * inset}px)`,
				height: `calc(100% - ${2 * inset}px)`,
				overflow: "visible",
				pointerEvents: "none",
			}}>
			{!corners ? (
				<rect
					width="100%"
					height="100%"
					rx={Math.max(0, radius - width / 2)}
					fill="none"
					style={stroke}
				/>
			) : (
				size && (
					<path
						d={roundedRectPath(size.width, size.height, corners)}
						fill="none"
						style={stroke}
					/>
				)
			)}
		</svg>
	)
}

export default DashedOutline
